// @ts-strict-ignore
import type { SyntheticEvent } from 'react';
import React, { useCallback } from 'react';

import type { Action } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import { getActionName } from 'flow/service/actions';
import {
  selectDirectDependentActions,
  selectHighlightedAction,
} from 'flow/store/redux/selectors';
import {
  setFocusedNodeId, setHighlightedNodeId, setHoveredNodeId,
} from 'flow/store/redux/slice';
import { StyledActionExtended, StyledActionWrapper } from 'flow/editor/styles';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import OutputAttributeExpressionTab from '../OutputAttributeExpressionTab';
import ThresholdsTab from '../ThresholdsTab';
import { StyledRuleContainer, StyledDependentsTabContainer, StyledDependentsContainer } from './styles';
import { ContainerType } from '../ThresholdsTab/types';

export default function DependentsTab() {
  const dispatch = useAppDispatch();
  const highlightedAction = useAppSelector(selectHighlightedAction);
  const focusedActionDependents = useAppSelector(selectDirectDependentActions);

  const handleSetHoveredAction = useCallback<(event: SyntheticEvent, action?: Action) => void>(
    (event, action = null) => {
      dispatch(setHoveredNodeId(action?.publicId ?? null));
      dispatch(setHighlightedNodeId(action?.publicId ?? null));
    }, [dispatch]);

  const HighlightedDependentContent = React.useMemo(() => {
    if (!highlightedAction) return null;

    switch (highlightedAction.type) {
      case ActionType.OUTPUT:
        return <OutputAttributeExpressionTab action={highlightedAction} editable={false} />;
      default:
        return (
          <ThresholdsTab
            action={highlightedAction}
            editable={false}
            containerType={ContainerType.TAB}
          />
        );
    }
  }, [highlightedAction]);

  return (
    <StyledDependentsTabContainer>
      <StyledDependentsContainer>
        {focusedActionDependents.length
          ? (focusedActionDependents.map(action => (
            <StyledActionWrapper
              key={action.publicId}
              onClick={() => dispatch(setFocusedNodeId(action.publicId))}
              onMouseEnter={event => handleSetHoveredAction(event, action)}
              onMouseLeave={event => handleSetHoveredAction(event)}
            >
              <StyledActionExtended action={action}>
                {getActionName(action)}
              </StyledActionExtended>
            </StyledActionWrapper>
          )))
          : (
            <StyledActionWrapper>
              This action has no dependents.
            </StyledActionWrapper>
          )}
      </StyledDependentsContainer>
      <StyledRuleContainer>
        {HighlightedDependentContent}
      </StyledRuleContainer>
    </StyledDependentsTabContainer>
  );
}
